import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { BackLink } from "../../components/ui/BackLink";
import { Icon } from "../../components/ui/Icon";
import { Inline } from "../../components/ui/Rich";
import { ApiError } from "../../lib/api";
import { libraryApi, type LibraryItem as Item } from "../../lib/libraryApi";
import { useToast } from "../goal/useToast";
import { changeOf, leadOf, norm, parseDoc, pieceSet, ruleWins, RULES_ID, summaryOf, type Block, type Doc, type Part, type Spot } from "./parts";
import { Compare, VersionList, whenOf, type Ask, type Ver, type VersionActs } from "../versions/Versions";
import { bodyOf, libLoad, Marks, previousVer, verOf } from "./ShelfParts";
import { labelOf, placeOf, previousOf, shelfItems, type ShelfItem } from "./shelf";
import { toolName } from "./tools";
import { useNative } from "../../../shared/phone/media";
import { ItemNative } from "./ItemNative";
import { LibrarySplit } from "./LibraryNative";
import { useCan } from "../../context/AuthContext";
import "../../styles/library.css";
import "../../styles/shelf.css";

const message = (e: unknown) => (e instanceof ApiError ? e.message : "That did not save. Try again.");

// --- One piece, and its box ---

const hold = (e: MouseEvent) => e.preventDefault();

function PieceBox({ initial, line, fresh, onDone, onCancel, as: T = "div" }: { initial: string; line: boolean; fresh: boolean; onDone: (v: string, next: boolean) => void; onCancel: () => void; as?: "div" | "li" | "p" }) {
  const [v, setV] = useState(initial);
  const box = useRef<HTMLTextAreaElement>(null);
  const done = useRef(false);
  useLayoutEffect(() => {
    const t = box.current;
    if (!t) return;
    t.focus();
    t.setSelectionRange(t.value.length, t.value.length);
  }, []);
  useLayoutEffect(() => {
    const t = box.current;
    if (!t) return;
    t.style.height = "auto";
    t.style.height = `${t.scrollHeight + 2}px`;
  }, [v]);
  const finish = (keep: boolean, next = false) => {
    if (done.current) return;
    done.current = true;
    if (keep) onDone(v, next);
    else onCancel();
  };
  return (
    <T
      className="ip-ed"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) finish(true);
      }}
    >
      <textarea
        ref={box}
        rows={1}
        aria-label={fresh ? "New line" : "Change this"}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            finish(false);
          } else if (e.key === "Enter" && !e.shiftKey && line) {
            e.preventDefault();
            finish(true, true);
          }
        }}
      />
      <span className="ip-edf">
        <span>{line ? "Enter adds the next line." : ""}</span>
        <button type="button" className="btn sm" onMouseDown={hold} onClick={() => finish(true)}>
          Done
        </button>
        <button type="button" className="btn sm ghost" onMouseDown={hold} onClick={() => finish(false)}>
          Cancel
        </button>
      </span>
    </T>
  );
}

function Table({ text }: { text: string }) {
  const rows = text
    .split("\n")
    .filter((r) => !/^\s*\|[\s\-:|]+\|\s*$/.test(r))
    .map((r) =>
      r
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((c) => c.trim()),
    );
  const [head, ...body] = rows;
  return (
    <div className="it-tbl">
      <table>
        <thead>
          <tr>
            {head?.map((c, i) => (
              <th key={i}>
                <Inline text={c} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>
                  <Inline text={c} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface Editing {
  /** Whether the person may change the words (an Editor's). */
  may: boolean;
  spot: Spot | null;
  open: (s: Spot) => void;
  close: () => void;
  save: (s: Spot, value: string, next: boolean) => void;
  busy: boolean;
}

/** A piece the owner can change in place; its pencil shows on hover. */
function Piece({ b, spot, ed, mine, lead, tag, id }: { b: Block; spot: Spot; ed: Editing; mine: boolean; lead?: string; tag: "p" | "li" | "div" | "h4"; id?: string }) {
  const list = b.t === "li" || b.t === "ol";
  if (ed.spot && !ed.spot.fresh && ed.spot.part === spot.part && ed.spot.bi === spot.bi)
    return <PieceBox as={tag === "li" ? "li" : "div"} initial={b.text} line={list} fresh={false} onDone={(v, next) => ed.save(spot, v, next)} onCancel={ed.close} />;
  const T = tag;
  const go = () => ed.may && !ed.busy && ed.open(spot);
  return (
    <T
      id={id}
      className={`ip-b${mine ? " mine" : ""}${b.lvl ? " l1" : ""}${lead ? " it-lead" : ""}${ed.may ? "" : " ro"}`}
      tabIndex={ed.may ? 0 : undefined}
      role={ed.may ? "button" : undefined}
      title={ed.may ? "Click to change" : undefined}
      onClick={(e: MouseEvent) => {
        if (!(e.target as HTMLElement).closest("a, button")) go();
      }}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          go();
        }
      }}
    >
      {ed.may && (
        <span className="ip-pen" aria-hidden="true">
          <Icon name="pen" />
        </span>
      )}
      {lead && <span className="it-lt">{lead}</span>}
      {b.t === "ol" && <span className="on">{b.num}.</span>}
      {b.t === "table" ? <Table text={b.text} /> : <Inline text={b.text} links />}
    </T>
  );
}

const AddLine = ({ onClick, label = "Add a line", className = "ip-add" }: { onClick: () => void; label?: string; className?: string }) => (
  <button type="button" className={className} onClick={onClick}>
    <Icon name="plus" />
    {label}
  </button>
);

/** A part's pieces: lists grouped, its first paragraph marked as the line In short shows when the summary is built. */
function Pieces({ part, ed, base, leadNum }: { part: Part; ed: Editing; base: Set<string> | null; leadNum: string | null }) {
  const out: ReactNode[] = [];
  const B = part.blocks;
  const fresh = ed.spot?.fresh && ed.spot.part === part.id ? ed.spot : null;
  const isMine = (b: Block) => !!base && !base.has(norm(b.text));
  let i = 0;
  while (i < B.length) {
    const b = B[i];
    if (b.t === "li" || b.t === "ol") {
      const kind = b.t;
      const items: ReactNode[] = [];
      let j = i;
      while (j < B.length && B[j].t === kind) {
        items.push(<Piece key={j} b={B[j]} spot={{ part: part.id, bi: j }} ed={ed} mine={isMine(B[j])} tag="li" />);
        if (fresh?.bi === j) items.push(<PieceBox key={`n${j}`} as="li" initial="" line fresh onDone={(v, next) => ed.save(fresh, v, next)} onCancel={ed.close} />);
        j++;
      }
      const last = j - 1;
      out.push(kind === "ol" ? <ol key={`l${i}`}>{items}</ol> : <ul key={`l${i}`}>{items}</ul>);
      if (ed.may && fresh?.bi !== last) out.push(<AddLine key={`a${i}`} onClick={() => ed.open({ part: part.id, bi: last, fresh: true })} />);
      i = j;
      continue;
    }
    const lead = i === 0 && leadNum && leadOf(part) === b ? `In short, line ${leadNum}` : undefined;
    out.push(<Piece key={i} b={b} spot={{ part: part.id, bi: i }} ed={ed} mine={isMine(b)} lead={lead} tag={b.t === "h" ? "h4" : b.t === "table" ? "div" : "p"} />);
    i++;
  }
  return <>{out}</>;
}

// --- The page ---

/** One skill or tool (`/library/:id`): on a phone or an iPad its native screen beside the shelf, else the page. */
export function LibraryItem() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const native = useNative();
  if (!native) return <ItemPage />;
  return <LibrarySplit sel={{ item: id }} detail={<ItemNative id={id} part={params.get("part")} />} />;
}

/** The page on a computer: your rules, In short built from the parts, the parts, and its versions. */
function ItemPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const back = ((location.state ?? {}) as { from?: { to: string; label: string } }).from ?? { to: "/library", label: "Library" };
  const { toast, say } = useToast();
  const [it, setIt] = useState<ShelfItem | null>(null);
  const [item, setItem] = useState<Item | null>(null);
  const [base, setBase] = useState<Set<string> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [spot, setSpot] = useState<Spot | null>(null);
  const [busy, setBusy] = useState(false);
  const [cmp, setCmp] = useState<{ from: Ver; to: Ver } | null>(null);
  const [sheet, setSheet] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const may = useCan()("editor");
  const open = params.get("part");

  const load = useCallback(async () => {
    try {
      const { item: asked } = await libraryApi.item(id);
      const shelf = shelfItems((await libraryApi.list()).items).find((x) => x.kind === asked.kind && x.name === asked.name) ?? null;
      const inUse = shelf && shelf.inUse.id !== asked.id ? (await libraryApi.item(shelf.inUse.id)).item : asked;
      setIt(shelf);
      setItem(inUse);
      // Why: the pieces the owner wrote are the ones not in the Flashadz version their words started from.
      const from = shelf && shelf.inUse.who === "you" ? shelf.versions.filter((v) => v.who === "flashadz" && v.at <= shelf.inUse.at).at(-1) : null;
      setBase(from ? pieceSet(parseDoc(await bodyOf(from.id))) : null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  }, [id]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 2400);
    return () => clearTimeout(t);
  }, [flash]);

  const doc: Doc | null = item ? parseDoc(item.body) : null;
  const goPart = (pid: string | null) => {
    const next = new URLSearchParams(params);
    if (pid) next.set("part", pid);
    else next.delete("part");
    setParams(next, { replace: true });
  };

  /** Save new words as the next version of the item in use, then show it. */
  const saveBody = async (body: string, note: string, after?: (d: Doc) => Spot | null) => {
    if (!item || !it) return;
    setBusy(true);
    try {
      const { item: saved } = await libraryApi.saveVersion(it.inUse.id, { body, note });
      setItem(saved);
      const nextSpot = after?.(parseDoc(saved.body)) ?? null;
      setSpot(nextSpot);
      navigate(`/library/${encodeURIComponent(saved.id)}${open ? `?part=${encodeURIComponent(open)}` : ""}`, { replace: true, state: location.state });
      say(`Saved as Yours v${saved.version}.`);
    } catch (e) {
      say(message(e), false);
      setSpot(null);
    } finally {
      setBusy(false);
    }
  };

  const ed: Editing = {
    may,
    spot,
    busy,
    open: setSpot,
    close: () => setSpot(null),
    save: (s, value, next) => {
      if (!doc || !item) return;
      const c = changeOf(item.body, doc, s, value);
      if (!c) return setSpot(null);
      if (c.lead) setFlash(s.part);
      const b = doc.parts.find((p) => p.id === s.part)?.blocks[s.bi];
      // Enter in a list line or a rule opens a fresh line after it.
      const after = (): Spot | null => {
        if (!next) return null;
        if (s.part === RULES_ID) return value.trim() ? { part: RULES_ID, bi: s.fresh ? s.bi + 1 : s.bi, fresh: true } : null;
        if (s.fresh) return { part: s.part, bi: s.bi + 1, fresh: true };
        return (b?.t === "li" || b?.t === "ol") && value.trim() ? { part: s.part, bi: s.bi, fresh: true } : null;
      };
      void saveBody(c.body, c.note, after);
    },
  };

  const acts: VersionActs & { keep: () => Promise<void> } = {
    busy,
    compare: (from, to) => setCmp({ from, to }),
    keep: async () => {
      if (!it?.waits) return;
      setBusy(true);
      try {
        await libraryApi.keepMine(it.inUse.id);
        say(`Kept ${labelOf(it.inUse).toLowerCase()}. Flashadz v${it.waits.n} stops waiting.`);
        setCmp(null);
        await load();
      } catch (e) {
        say(message(e), false);
      } finally {
        setBusy(false);
      }
    },
    use: async (v) => {
      if (!it) return;
      setBusy(true);
      try {
        const saved =
          it.waits?.id === v.id
            ? (await libraryApi.takeDefault(it.inUse.id)).item
            : (await libraryApi.saveVersion(it.inUse.id, { body: await bodyOf(v.id), note: `Back to ${v.label}` })).item;
        setCmp(null);
        setSheet(false);
        say(it.waits?.id === v.id ? `Updated to Flashadz v${v.n}. New goals get it from now.` : `${v.label}'s words are back, as Yours v${saved.version}.`);
        navigate(`/library/${encodeURIComponent(saved.id)}`, { replace: true, state: location.state });
      } catch (e) {
        say(e instanceof ApiError && /Nothing changed/.test(e.message) ? "Those are the words already in use." : message(e), false);
      } finally {
        setBusy(false);
      }
    },
  };

  if (err)
    return (
      <section className="wrap ga it">
        <p className="load-note">{err}</p>
      </section>
    );
  if (!it || !item || !doc)
    return (
      <section className="wrap ga it">
        <p className="load-note">Loading…</p>
      </section>
    );

  const vers = it.versions.map(verOf);
  const firstYours = it.versions.find((v) => v.who === "you");
  const nextYours = it.versions.filter((v) => v.who === "you").length + 1;
  const ask = (v: Ver): Ask =>
    it.waits?.id === v.id
      ? { q: `Use ${v.label}?`, body: "New goals get it from now. Goals already running keep theirs.", button: `Use v${v.n}` }
      : { q: `Use the words of ${v.label}?`, body: `They become Yours v${nextYours}, in use from now. Goals already running keep theirs. Nothing is deleted.`, button: "Use these words" };
  const list = {
    versions: vers,
    current: it.inUse.id,
    waits: it.waits?.id ?? null,
    started: firstYours ? (previousOf(it, firstYours)?.id ?? null) : null,
    marks: <Marks it={it} />,
    previous: (v: Ver) => previousVer(it, v),
    ask,
    acts,
  };

  const sum = summaryOf(doc);
  const wins = ruleWins(doc);
  const tools = Array.isArray(item.meta.allowedTools) ? (item.meta.allowedTools as string[]) : [];
  const kindWord = it.kind === "skill" ? "Skills" : it.kind === "method" ? "Tools" : "Library";
  const nums = new Map(doc.parts.map((p, i) => [p.id, String(i + 1).padStart(2, "0")]));
  const ruleSpot = spot?.part === RULES_ID ? spot : null;

  const rulesBox = (
    <>
      {!doc.rules.length && !ruleSpot ? (
        may && (
          <button type="button" className="it-r0" onClick={() => setSpot({ part: RULES_ID, bi: -1, fresh: true })}>
            <Icon name="shield" />
            <b>Your rules</b>
            <span>
              <Icon name="plus" />
              Add a rule in your words. It wins over anything below.
            </span>
          </button>
        )
      ) : (
        <section className="it-rules" aria-labelledby="r-h">
          <div className="it-rh">
            <h2 id="r-h">
              <Icon name="shield" />
              Your rules
            </h2>
            <p>In your words. They win over anything below.</p>
          </div>
          <ul className="it-rl">
            {ruleSpot?.fresh && ruleSpot.bi === -1 && <PieceBox as="li" initial="" line fresh onDone={(v, next) => ed.save(ruleSpot, v, next)} onCancel={ed.close} />}
            {doc.rules.map((r, ri) => {
              const over = doc.parts.filter((p) => wins[p.id]?.includes(ri));
              return (
                <Fragment key={ri}>
                  <Piece b={r} spot={{ part: RULES_ID, bi: ri }} ed={ed} mine={false} tag="li" id={`rule-${ri}`} />
                  {over.length > 0 && !(ruleSpot && !ruleSpot.fresh && ruleSpot.bi === ri) && (
                    <li className="it-ro">
                      Wins over{" "}
                      {over.map((p, k) => (
                        <span key={p.id}>
                          {k > 0 && ", "}
                          <button type="button" onClick={() => goPart(p.id)}>
                            {p.title}
                          </button>
                        </span>
                      ))}
                    </li>
                  )}
                  {ruleSpot?.fresh && ruleSpot.bi === ri && <PieceBox as="li" initial="" line fresh onDone={(v, next) => ed.save(ruleSpot, v, next)} onCancel={ed.close} />}
                </Fragment>
              );
            })}
          </ul>
          {may && !ruleSpot?.fresh && <AddLine label="Add a rule" onClick={() => setSpot({ part: RULES_ID, bi: doc.rules.length - 1, fresh: true })} />}
        </section>
      )}
    </>
  );

  return (
    <section className="wrap ga it">
      <header className="ph it-head">
        <div className="ctx">
          <BackLink to={back.to} label={back.label} />
          <span className="crumbs">
            <b>{kindWord}</b>
            {placeOf(it) !== "Other" && <span>{placeOf(it)}</span>}
          </span>
        </div>
        <h1 className="it-t">{it.title}</h1>
        {it.line && <p className="it-sub">{it.line}</p>}
        <div className="it-meta">
          <button type="button" className="it-vb" onClick={() => setSheet(true)} aria-label="Versions">
            <Marks it={it} large />
            <span className={it.inUse.who === "you" ? "y" : ""}>{labelOf(it.inUse)}</span>
            <span className="d">{whenOf(it.inUse.at).split(",")[0]}</span>
          </button>
        </div>
      </header>
      <div className="it-grid">
        <div>
          {it.waits && (
            <div className="it-up">
              <span className="ki">
                <Icon name="spark" />
              </span>
              <div>
                <b>Flashadz v{it.waits.n} is out</b>
                {it.waits.note && <p>{it.waits.note}</p>}
              </div>
              <div className="row">
                <button type="button" className="btn sm ghost" onClick={() => it.waits && setCmp({ from: verOf(it.inUse), to: verOf(it.waits) })}>
                  What changed
                </button>
                {may && (
                  <>
                    <button type="button" className="btn sm ghost" disabled={busy} onClick={() => void acts.keep()}>
                      Keep {labelOf(it.inUse).toLowerCase()}
                    </button>
                    <button type="button" className="btn sm" disabled={busy} onClick={() => it.waits && void acts.use(verOf(it.waits))}>
                      Update
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          {rulesBox}
          {sum.length > 0 && (
            <section className="it-gist" aria-labelledby="g-h">
              <div className="it-gh">
                <h2 className="it-eb" id="g-h">
                  In short
                </h2>
                {!doc.written && <p className="it-gnote">Each line is the first line of a part below</p>}
              </div>
              <ol className="it-sum">
                {sum.map((s, i) => {
                  const ri = s.part ? wins[s.part.id]?.[0] : undefined;
                  const mine = !!base && !base.has(norm(s.text));
                  return (
                    <li key={i} className={`${mine ? "mine" : ""}${s.part && flash === s.part.id ? " flash" : ""}`}>
                      <button type="button" className="sum-go" onClick={() => s.part && goPart(s.part.id)} disabled={!s.part}>
                        <span className="n">{String(i + 1).padStart(2, "0")}</span>
                        <span className="t">
                          <Inline text={s.text} />
                        </span>
                      </button>
                      {ri !== undefined && (
                        <a className="sum-win" href={`#rule-${ri}`}>
                          <Icon name="shield" />
                          Your rule wins here
                        </a>
                      )}
                    </li>
                  );
                })}
              </ol>
            </section>
          )}
          <section className="it-parts" aria-labelledby="p-h">
            <div className="it-ph">
              <h2 id="p-h">All parts</h2>
              <span>{doc.parts.length} parts</span>
            </div>
            <div className="it-acc">
              {doc.parts.map((p) => {
                const isOpen = open === p.id;
                const ri = wins[p.id]?.[0];
                const edited = !!base && p.blocks.some((b) => !base.has(norm(b.text)));
                return (
                  <div className={`it-pt${isOpen ? " open" : ""}`} id={`pt-${p.id}`} key={p.id}>
                    <h3>
                      <button type="button" aria-expanded={isOpen} onClick={() => goPart(isOpen ? null : p.id)}>
                        <span className="it-pt-n">{nums.get(p.id)}</span>
                        <span>{p.title}</span>
                        <span className="it-pm">
                          {ri !== undefined && (
                            <span className="it-wn">
                              <Icon name="shield" />
                              <span>Your rule wins here</span>
                            </span>
                          )}
                          {edited && (
                            <span className="it-mk">
                              <span>Your words</span>
                            </span>
                          )}
                        </span>
                        <Icon name="chev" />
                      </button>
                    </h3>
                    {isOpen && (
                      <div className="it-pt-b">
                        {ri !== undefined && (
                          <div className="it-win">
                            <Icon name="shield" />
                            <p>
                              <b>Your rule wins here.</b> <Inline text={doc.rules[ri].text} />{" "}
                              <a className="it-wl" href={`#rule-${ri}`}>
                                See your rule
                              </a>
                            </p>
                          </div>
                        )}
                        <div className="it-doc">
                          <p className="ip-tip">
                            <Icon name="pen" />
                            Click any line to change it
                          </p>
                          <Pieces part={p} ed={ed} base={base} leadNum={doc.written ? null : (nums.get(p.id) ?? null)} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {tools.length > 0 && (
                <div className={`it-pt${open === "what-it-can-do" ? " open" : ""}`}>
                  <h3>
                    <button type="button" aria-expanded={open === "what-it-can-do"} onClick={() => goPart(open === "what-it-can-do" ? null : "what-it-can-do")}>
                      <span className="it-pt-n">
                        <Icon name="tool" />
                      </span>
                      <span>What it can do</span>
                      <span className="it-pt-n">{tools.length}</span>
                      <Icon name="chev" />
                    </button>
                  </h3>
                  {open === "what-it-can-do" && (
                    <div className="it-pt-b">
                      <div className="it-chips">
                        {tools.map((t) => (
                          <span className="chip" key={t}>
                            {toolName(t)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
        <aside className="vt" aria-label="Versions">
          <VersionList {...list} where="side" />
        </aside>
      </div>
      {sheet && (
        <>
          <div className="ov-scrim in" onClick={() => setSheet(false)} />
          <section className="vs in" role="dialog" aria-modal="true" aria-label="Versions">
            <div className="grab" />
            <button type="button" className="cm-x" aria-label="Close" onClick={() => setSheet(false)}>
              <Icon name="x" />
            </button>
            <VersionList {...list} where="sheet" />
          </section>
        </>
      )}
      {cmp && (
        <Compare
          versions={vers}
          from={cmp.from}
          to={cmp.to}
          load={libLoad}
          onPick={(from, to) => setCmp({ from, to })}
          onClose={() => setCmp(null)}
          foot={
            !may ? undefined : it.waits && cmp.to.id === it.waits.id ? (
              <>
                <span className="note">Nothing updates until you say so.</span>
                <button type="button" className="btn sm ghost" disabled={busy} onClick={() => void acts.keep()}>
                  Keep {labelOf(it.inUse).toLowerCase()}
                </button>
                <button type="button" className="btn sm" disabled={busy} onClick={() => it.waits && void acts.use(verOf(it.waits))}>
                  Update to v{it.waits.n}
                </button>
              </>
            ) : cmp.to.id !== it.inUse.id && previousVer(it, cmp.to)?.id === cmp.from.id ? (
              <>
                <span className="note">Going back makes a new version. Nothing is deleted.</span>
                <button type="button" className="btn sm" disabled={busy} onClick={() => void acts.use(cmp.to)}>
                  <Icon name="again" />
                  Use {cmp.to.label}
                </button>
              </>
            ) : undefined
          }
        />
      )}
      {toast}
    </section>
  );
}

